import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useIntroCallRequests from '@/api/useIntroCallRequests.ts';
import useUpdateIntroCallStatus from '@/api/useUpdateIntroCallStatus.ts';
import dayjs from 'dayjs';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import { createColumnHelper } from '@tanstack/react-table';
import type { IntroCallRequest } from '@/api/useIntroCallRequests.ts';
import Routes from '@/router/Routes.ts';
import { formatCareType } from '@/utils/formatCareType.ts';

const columnHelper = createColumnHelper<IntroCallRequest>();

const getStatusColor = (status: string) => {
  switch (status) {
    case 'accepted':
      return 'text-[#00A96E]';
    case 'pending':
      return 'text-[#FFBE00]';
    case 'declined':
      return 'text-[#FF5861]';
    case 'completed':
      return 'text-[#00A96E]';
    case 'not_completed':
      return 'text-[#FF5861]';
    default:
      return 'text-gray-600';
  }
};

const getStatusLabel = (status: string) => {
  if (status === 'not_completed') return 'Not Completed';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const IntroCallsPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: requests, isLoading, isError } = useIntroCallRequests();
  const { mutate: updateStatus, isPending: isUpdating } = useUpdateIntroCallStatus();

  const handleAccept = (requestId: string) => {
    updateStatus({ request_id: requestId, status: 'accepted' });
  };

  const handleDecline = (requestId: string) => {
    updateStatus({ request_id: requestId, status: 'declined' });
  };

  const handleShowDetails = (introCallId: string) => {
    navigate(Routes.INTRO_CALL_DETAILS.replace(':introCallId', introCallId));
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('other_user_name', {
        header: 'Family',
        cell: (info) => {
          return <span className="font-bold">{info.getValue()}</span>;
        },
      }),
      columnHelper.accessor('care_type', {
        header: 'Care Type',
        cell: (info) => {
          const careType = info.getValue();
          return <span className="text-sm font-bold">{formatCareType(careType)}</span>;
        },
      }),
      columnHelper.accessor('preferred_date', {
        header: 'Date & Time',
        cell: (info) => {
          const request = info.row.original;
          const timeRange = request.preferred_time_from && request.preferred_time_to
            ? `${request.preferred_time_from} - ${request.preferred_time_to}`
            : request.preferred_time_from || '';
          return (
            <span className="text-sm">
              {request.preferred_date
                ? `${dayjs(request.preferred_date).format('MMMM DD, YYYY')} — ${timeRange}`
                : 'Not specified'}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'details',
        header: 'Details',
        cell: (info) => {
          const request = info.row.original;
          return (
            <button 
              onClick={() => handleShowDetails(request.id)}
              className="text-black hover:opacity-70 text-sm font-bold cursor-pointer"
            >
              Show details
            </button>
          );
        },
      }),
      columnHelper.accessor('status', {
        header: 'Action',
        cell: (info) => {
          const request = info.row.original;
          
          // Carer receives request - show Accept/Decline buttons
          if (!request.is_requester && request.status === 'pending') {
            return (
              <div className="flex gap-4">
                <button
                  onClick={() => handleAccept(request.id)}
                  disabled={isUpdating}
                  className="text-[#00A96E] hover:opacity-70 font-bold disabled:opacity-50 cursor-pointer"
                >
                  Accept
                </button>
                <span className="text-gray-300 font-bold">/</span>
                <button
                  onClick={() => handleDecline(request.id)}
                  disabled={isUpdating}
                  className="text-[#FF5861] hover:opacity-70 font-bold disabled:opacity-50 cursor-pointer"
                >
                  Decline
                </button>
              </div>
            );
          }
          
          return (
            <span className={`font-semibold ${getStatusColor(request.status)}`}>
              {getStatusLabel(request.status)}
            </span>
          );
        },
      }),
    ],
    [isUpdating]
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <div className="text-xl font-semibold">{t('introCallsPage.title')}</div>
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-lg mb-4" />
          <div className="h-32 bg-gray-200 rounded-lg mb-4" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <div className="text-xl font-semibold">{t('introCallsPage.title')}</div>
        <div className="text-red-500">{t('introCallsPage.errorLoading')}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full">
      <div>
        <h1 className="text-2xl font-bold">{t('introCallsPage.title')}</h1>
        <p className="text-gray-600 text-sm mt-1">
          {t('introCallsPage.subtitle')}
        </p>
      </div>

      {!requests || requests.length === 0 ? (
        <div className="bg-[#F6F6F4] rounded-lg py-6 flex items-center justify-center w-full mt-8">
          <p className="text-base font-semibold text-center text-black">
            {t('introCallsPage.noRequests')}
          </p>
        </div>
      ) : (
        <div className="w-full overflow-x-auto">
          <DataTable 
            data={requests} 
            columns={columns} 
            wrapperClassName="border border-gray-200 rounded-lg min-w-[600px]" 
            rowClassName={(index: number) => index % 2 === 0 ? 'bg-[#F6F6F4]' : 'bg-white'}
          />
        </div>
      )}
    </div>
  );
};

export default IntroCallsPage;
